import React from 'react';
import {
    Box, Modal, Fade, Grid, Button, Table, TableCell, TableHead, TableRow, TableContainer, Paper, IconButton,
} from '@mui/material';
import styled from '@emotion/styled';
import swal from 'sweetalert2';
import CloseIcon from '@mui/icons-material/Close';
import { fetchAuthSession } from 'aws-amplify/auth';
import { inputDataSavesByMailAddressAndConditionsName } from 'graphql/queries';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { deleteInputDataSave } from 'graphql/mutations';
import { DeleteInputDataSaveMutationVariables } from 'API';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { sessionDisconnectedCheck } from 'shared/sessionDisconnectedCheck';
import { SystemErrorTitle, SystemErrorMsg } from '../../constants';
import { BackdropLoading } from 'shared/backdropLoading';

interface OwnProps {
    isOpen: boolean,
    yearEdition: any,
    handleClose: (isOpen: boolean) => void,
    handleLoad: (tableValue: { [key: string]: any }) => void,
}

interface ConditionsData {
    id: string,
    registDateTime: string,
    conditionsName: string,
    financialAnalysisData: string,
    version: number
}

const modalStyle = {
    position: 'absolute',
    top: '55%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    bgcolor: 'background.paper',
    textAlign: 'center',
    borderRadius: '15px'
};

const ModalLabel = styled(Box)(({ }) => ({
    backgroundColor: '#41DBCE',
    fontSize: '1.2rem',
    color: '#FFFFFF',
    height: '2rem',
    paddingTop: '0.5rem',
    borderRadius: '15px 15px 0 0',
    textAlign: 'left',
    paddingLeft: '1rem'
}));

const ConditionNameText = styled(Grid)(({ }) => ({
    display: 'flex',
    alignItems: 'center',
}));

const SaveButton = styled(Button)(({ }) => ({
    backgroundColor: '#01CFBE',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    height: '1.8rem',
    width: '5rem',
    borderRadius: '100rem',
    margin: '0.55rem'
}));

const DeleteButton = styled(Button)(({ }) => ({
    backgroundColor: '#E97132',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    height: '1.8rem',
    width: '5rem',
    borderRadius: '100rem',
    marginTop: '0.55rem',
    marginBottom: '0.55rem',
}));

const screenId = "U042"

const LoadConditionModal = (props: OwnProps) => {
    const navigate = useNavigate();
    const { isOpen, yearEdition, handleClose } = props
    const [open, setOpen] = React.useState(isOpen);
    const [saveDataList, setSaveDataList] = React.useState<ConditionsData[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);

    const getDataList = async () => {
        setLoading(true)
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "getDataList", yearEdition, navigate)) {
            return
        }
        const session = await fetchAuthSession();
        let filter = {
            and: [
                { _deleted: { ne: true } }
            ]
        };
        let allItems: any[] = [];
        let nextToken: string | null = null;
        try {
            do {
                const client = generateClient();
                const result: GraphQLResult<any> = await client.graphql({
                    query: inputDataSavesByMailAddressAndConditionsName,
                    variables: {
                        mailAddress: session?.tokens?.idToken?.payload.email?.toString() || "",
                        filter: filter,
                        nextToken: nextToken,
                        limit: 500
                    }
                });
                const items = result.data?.inputDataSavesByMailAddressAndConditionsName?.items || [];
                nextToken = result.data?.inputDataSavesByMailAddressAndConditionsName?.nextToken || null;
                allItems = allItems.concat(items)
            } while (nextToken);
            const sortedItems = allItems.sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            const conditionsDataList: ConditionsData[] = []
            sortedItems.forEach((v: { [key: string]: any }) => {
                // 取得してきた日時はUTCなのでJSTに変換する
                const formatDate = (isoString: string) => {
                    return dayjs(isoString).format('YYYY/MM/DD HH:mm');
                };
                let registDateTime = formatDate(v.updatedAt);
                conditionsDataList.push({
                    id: v.id,
                    registDateTime: registDateTime,
                    conditionsName: v.conditionsName || '',
                    financialAnalysisData: v.financialAnalysisData || '',
                    version: v._version
                })
            })
            setSaveDataList(conditionsDataList)
            setLoading(false)
        } catch (error) {
            navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            getDataList()
        }
        setOpen(isOpen)
    }, [isOpen])

    const handleLoad = async (v: string) => {
        setLoading(true)
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "handleLoad", yearEdition, navigate)) {
            return
        }
        setLoading(false)
        swal.fire({
            text: '保存データの読込が完了しました',
            icon: 'success',
            confirmButtonText: 'OK'
        }).then((result) => {
            if (result.isConfirmed) {
                props.handleLoad(JSON.parse(v))
            }
        });
    }
    const handleDelete = async (id: string, version: number) => {
        setLoading(true)
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "handleDelete", yearEdition, navigate)) {
            return
        }
        const deleteConditions = async () => {
            const input: DeleteInputDataSaveMutationVariables = {
                input: {
                    id: id,
                    _version: version
                },
            };
            try {
                const client = generateClient({ authMode: 'userPool' });
                await client.graphql({
                    query: deleteInputDataSave,
                    variables: input,
                });
                setLoading(false)
                swal.fire({
                    text: '保存データの削除が完了しました',
                    icon: 'success',
                    confirmButtonText: 'OK'
                }).then(() => {
                    getDataList()
                });
            } catch (error) {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
        };
        deleteConditions()
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
                sx={{
                    zIndex: 900
                }}
            >
                <Fade in={open}>
                    <Box sx={modalStyle} tabIndex={-1}>
                        <ModalLabel>
                            保存データ読込・削除
                            <IconButton onClick={() => handleClose(false)} sx={{ position: 'absolute', top: '1px', right: '1rem' }}>
                                <CloseIcon style={{ color: '#fff' }} />
                            </IconButton>
                        </ModalLabel>
                        <Box sx={{ marginTop: '1rem', color: '#808080', fontSize: '0.9rem' }}>
                            読み込む保存データを選択してください
                        </Box>
                        <Box p={5}>
                            <TableContainer component={Paper} sx={{ maxHeight: '17.2rem' }}>
                                <Table stickyHeader sx={{ borderCollapse: 'separate' }}>
                                    <TableHead>
                                        <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid gray', height: '2rem', fontSize: '1rem', paddingLeft: '0', paddingRight: '0', paddingTop: '0.2rem', paddingBottom: '0.2rem', textAlign: 'center' } }}>
                                            <TableCell style={{ width: '30%', backgroundColor: '#00A99C', color: '#FFFFFF' }}>保存日時</TableCell>
                                            <TableCell style={{ width: '70%', backgroundColor: '#00A99C', color: '#FFFFFF' }}>保存名</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    {saveDataList.map((row) => (
                                        <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid gray', height: '0.3rem', fontSize: '0.8rem', paddingLeft: '0.5rem', paddingRight: '0.1rem', paddingTop: '0', paddingBottom: '0', } }}>
                                            <TableCell sx={{ textAlign: 'left' }}>{row.registDateTime}</TableCell>
                                            <TableCell sx={{ textAlign: 'left' }}>
                                                <Grid container>
                                                    <ConditionNameText size={{xs:6, md:8}} sx={{ paddingRight: '0.2rem' }}>
                                                        {row.conditionsName}
                                                    </ConditionNameText>
                                                    <Grid size={{xs:6, md:4}}>
                                                        <SaveButton variant="contained" color="secondary" onClick={() => handleLoad(row.financialAnalysisData)}>読込</SaveButton>
                                                        <DeleteButton variant="contained" color="secondary" onClick={() => handleDelete(row.id, row.version)}>削除</DeleteButton>
                                                    </Grid>
                                                </Grid>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </Table>
                            </TableContainer>
                        </Box>
                        <Box p={2}>
                            <span onClick={() => handleClose(false)} style={{ cursor: 'pointer' }}>
                                <img src={`${process.env.PUBLIC_URL}/Icons/returnIcon.svg`} width="13px" />&nbsp;&nbsp;戻る
                            </span>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
}

export default LoadConditionModal;
